import {type ReactNode, useMemo, useState} from 'react'
import {useEventCallback} from 'usehooks-ts'

import {DEV_OVERLAY} from '#/lib/constants'
import {DevStepPicker} from '#/screens/SignUpWizard/DevStepPicker'
import {PageShell} from '#/screens/SignUpWizard/primitives'
import {StepCongrats} from '#/screens/SignUpWizard/StepCongrats'
import {StepInstallWid} from '#/screens/SignUpWizard/StepInstallWid'
import {StepVerifyHuman} from '#/screens/SignUpWizard/StepVerifyHuman'
import {StepVerifying} from '#/screens/SignUpWizard/StepVerifying'
import {StepVerifyingMobile} from '#/screens/SignUpWizard/StepVerifyingMobile'
import {StepWidSetup} from '#/screens/SignUpWizard/StepWidSetup'
import {StepWidSetupMobile} from '#/screens/SignUpWizard/StepWidSetupMobile'
import {useWidVerificationAutoAdvance} from '#/screens/SignUpWizard/useWidVerificationAutoAdvance'
import {useIsMobileWeb} from '#/alf'
import {Loader} from '#/components/Loader'
import {useGeolocationServiceResponse} from '#/geolocation/service'
import {useSemanticTokens} from '#/wsocial/service/design-system'

interface WIDUpgradeModalProps {
  onClose: () => void
}

type StepType =
  | 'verifyHuman'
  | 'installWid'
  | 'widSetup'
  | 'verifying'
  | 'congrats'

const DEV_STEPS: StepType[] = [
  'verifyHuman',
  'installWid',
  'widSetup',
  'verifying',
  'congrats',
]

export function WIDUpgradeModal({onClose}: WIDUpgradeModalProps) {
  const isMobileWeb = useIsMobileWeb()
  const geoResponse = useGeolocationServiceResponse()
  const [step, setStep] = useState<StepType>('verifyHuman')
  const geoRestricted = geoResponse?.requiredValidation ?? false
  const go = useEventCallback((next: StepType) => setStep(next))

  useWidVerificationAutoAdvance({
    step,
    onScanned: () => go('verifying'),
    onVerified: () => go('congrats'),
  })

  const Element = useMemo(() => {
    switch (step) {
      case 'verifyHuman':
        return (
          <StepVerifyHuman
            onSkip={geoRestricted ? undefined : onClose}
            onNext={() => go('installWid')}
          />
        )
      case 'installWid':
        return (
          <StepInstallWid
            geoRestricted={geoRestricted}
            onBack={onClose}
            onSkip={geoRestricted ? undefined : onClose}
            onNext={() => go('widSetup')}
          />
        )
      case 'widSetup':
        if (isMobileWeb) {
          return (
            <StepWidSetupMobile
              onBack={() => go('installWid')}
              onSkip={geoRestricted ? undefined : onClose}
            />
          )
        }
        return (
          <StepWidSetup
            onBack={() => go('installWid')}
            onSkip={geoRestricted ? undefined : onClose}
          />
        )
      case 'verifying':
        if (isMobileWeb) {
          return (
            <StepVerifyingMobile onSkip={onClose} allowSkip={!geoRestricted} />
          )
        }
        return <StepVerifying onSkip={onClose} allowSkip={!geoRestricted} />
      case 'congrats':
        return <StepCongrats onSkip={onClose} onNext={onClose} />
    }
  }, [step, onClose, isMobileWeb, geoRestricted, go])

  if (!geoResponse) {
    return (
      <Overlay>
        <PageShell>
          <Loader size="xl" />
        </PageShell>
      </Overlay>
    )
  }

  return (
    <>
      {DEV_OVERLAY && (
        <DevStepPicker
          steps={DEV_STEPS}
          step={step}
          go={s => go(s as StepType)}
        />
      )}
      <Overlay>{Element}</Overlay>
    </>
  )
}

function Overlay({children}: {children: ReactNode}) {
  const tokens = useSemanticTokens()
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        backgroundColor: tokens.onboarding.bg,
        overflowY: 'auto',
      }}>
      {children}
    </div>
  )
}
